import { useState } from "react";
import { apiCall } from "../services/api-service";

export const useExcelActions = (moduleName, onSuccess, snackbarHandlers, reportType) => {
  const [uploadLoading, setUploadLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const { setSnackbarMessage, setSnackbarStatus, handleSnackbarShow } = snackbarHandlers;

  const handleFileUpload = async (e) => {
    const file = e?.target?.files ? e.target.files[0] : e;
    if (!file) return;

    if (e?.target) {
      e.target.value = "";
    }
    setUploadLoading(true);
    try {
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result.split(",")[1]);
      });

      const response = await apiCall("post", `${moduleName}_upload`, {
        file: base64,
      });

      setSnackbarMessage(response?.data?.message ?? "Something went wrong");
      setSnackbarStatus(response?.data?.success === true);
      handleSnackbarShow();

      if (response?.data?.base64) {
        const link = document.createElement("a");
        link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response.data.base64}`;
        const isSuccess = response?.data?.success === true;
        const prefix = isSuccess ? `${moduleName}_Upload_Report` : `${moduleName}_Upload_Error_Report`;
        const fileName = response.data.file_name || response.data.filename || `${prefix}_${Date.now()}.xlsx`;
        link.download = fileName;
        link.click();
      }

      if (response?.data?.success === true && onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Upload error:", error);
      const errMsg = error?.response?.data?.message ?? "Something went wrong";
      setSnackbarMessage(errMsg);
      setSnackbarStatus(false);
      handleSnackbarShow();
    } finally {
      setUploadLoading(false);
    }
  };

  const downloadTemplate = async () => {
    setTemplateLoading(true);
    try {
      const response = await apiCall("get", `template_download/${moduleName}_upload`);
      if (response?.status === 200 && response.data?.success === true) {
        const base64String = response.data.base64;
        const link = document.createElement("a");
        link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64String}`;
        link.download = response.data.file_name || response.data.filename || `${moduleName}_Template_${Date.now()}.xlsx`;
        link.click();
      }
      setSnackbarMessage(response?.data?.message ?? "Something went wrong");
      setSnackbarStatus(response?.data?.success === true);
      handleSnackbarShow();
    } catch (error) {
      console.error("Template download error:", error);
      const errMsg = error?.response?.data?.message ?? "Something went wrong";
      setSnackbarMessage(errMsg);
      setSnackbarStatus(false);
      handleSnackbarShow();
    } finally {
      setTemplateLoading(false);
    }
  };

  /**
   * Download a masters report for this module.
   * Hits masters_report?type=<reportType>&search=<search>&status=<status>.
   * The report type (e.g. "teams", "approval_configurations") is distinct from
   * moduleName, so it is taken from `reportType` (falls back to the arg).
   */
  const downloadReport = async ({ type, search = "", status = "" } = {}) => {
    const resolvedType = type || reportType;
    setReportLoading(true);
    try {
      const response = await apiCall("get", "masters_report", null, {
        params: { type: resolvedType, search, status },
      });
      if (response?.status === 200 && response.data?.success === true && response.data?.base64) {
        const link = document.createElement("a");
        link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response.data.base64}`;
        link.download = response.data.file_name || response.data.filename || `${resolvedType}_Report_${Date.now()}.xlsx`;
        link.click();
      }
      setSnackbarMessage(response?.data?.message ?? "Something went wrong");
      setSnackbarStatus(response?.data?.success === true);
      handleSnackbarShow();
    } catch (error) {
      console.error("Report download error:", error);
      const errMsg = error?.response?.data?.message ?? "Something went wrong";
      setSnackbarMessage(errMsg);
      setSnackbarStatus(false);
      handleSnackbarShow();
    } finally {
      setReportLoading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" || file.type === "application/vnd.ms-excel")) {
      handleFileUpload(file);
    }
  };

  return {
    uploadLoading,
    templateLoading,
    reportLoading,
    handleFileUpload,
    downloadTemplate,
    downloadReport,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    isDragging,
  };
};
